// Client-side image compression before upload. Field reliability lever: a raw
// phone photo is 2-8 MB; downscaling to a ~1280px longest side + JPEG q0.70
// brings a KYC/POD capture to ~0.2-0.6 MB, so uploads are fast and resilient on
// flaky field connections (and stay well under the backend's 10 MiB cap). 1280px
// keeps the DPI number/text legible for the operator (Renap cotejo is F2).
//
// Defensive: on any failure (decode error, no canvas) it falls back to the
// original file — a slightly larger upload beats blocking the ambassador.

const MAX_DIMENSION = 1280;
const JPEG_QUALITY = 0.7;

export const compressImage = async (
  file: Blob,
  maxDimension: number = MAX_DIMENSION,
  quality: number = JPEG_QUALITY,
): Promise<Blob> => {
  try {
    const bitmap = await loadBitmap(file);
    const { width, height } = scaledSize(bitmap.width, bitmap.height, maxDimension);

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, width, height);

    const blob = await canvasToBlob(canvas, quality);
    // Guard: if compression somehow produced a bigger blob, keep the smaller one.
    return blob && blob.size > 0 && blob.size < file.size ? blob : file;
  } catch {
    return file;
  }
};

const loadBitmap = async (file: Blob): Promise<ImageBitmap | HTMLImageElement> => {
  if (typeof createImageBitmap === "function") {
    return await createImageBitmap(file);
  }
  return await new Promise<HTMLImageElement>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
};

const scaledSize = (
  w: number,
  h: number,
  maxDimension: number,
): { width: number; height: number } => {
  const longest = Math.max(w, h);
  if (longest <= maxDimension) return { width: w, height: h };
  const ratio = maxDimension / longest;
  return { width: Math.round(w * ratio), height: Math.round(h * ratio) };
};

const canvasToBlob = async (
  canvas: HTMLCanvasElement,
  quality: number,
): Promise<Blob | null> =>
  await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
